import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import Layout from "../common/layout";
import { FaFileInvoice } from "react-icons/fa";
import { useSelector } from "react-redux";

const RequestQuoteListing = ({ quotesToken }) => {
  const [quotes, setQuotes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const user = useSelector((state) => state.user);
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();

  const fetchQuotes = async () => {
    loader(true);
    await ApiClient.get("request-quote/listing", {
      email: user?.email,
      status: "request",
    })
      .then((res) => {
        if (res?.success) {
          const data = res?.data?.data || [];
          const filtered = data.filter((item) => item.status !== "reject");
          setQuotes(filtered || []);
        }
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const openViewModal = (quote) => {
    setSelectedQuote(quote);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedQuote(null);
  };

  const filtered = quotes.filter((est) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      est.customer_name?.toLowerCase().includes(q) ||
      est.email?.toLowerCase().includes(q) ||
      est.status?.toLowerCase().includes(q);

    const matchesStatus =
      statusFilter === "" ? true : est.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const allStatus = [
    { name: "Pending", id: "request" },
    { name: "Accepted", id: "accepted" },
    { name: "Rejected", id: "rejected" },
    { name: "Negotiate", id: "negotiate" },
  ];

  return (
    <>
      <div className="container mx-auto">
        <div className="">
          <div className="flex justify-between flex-wrap gap-3 mb-4">
            <div className="relative w-full">
              <svg
                className="absolute left-6 top-[27px] -translate-y-1/2 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                type="text"
                placeholder="Search by customer name, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-full pl-11 pr-12 py-4 text-sm text-gray-800 outline-none bg-[#F8F8F8] border border-[#DADADA] transition-all"
              />
              <div className="absolute top-[8px] right-[8px] w-[38px] h-[38px] rounded-full flex justify-center items-center bg-[#115B9D] cursor-pointer hover:opacity-80">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <g clip-path="url(#clip0_163_465)">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7.29936 14.6456C5.35442 14.6456 3.51494 13.884 2.14411 12.5015C0.76157 11.1189 0 9.29116 0 7.34623C0 5.38958 0.76157 3.56181 2.14411 2.17927C3.51494 0.796729 5.35442 0.046875 7.29936 0.046875C9.24429 0.046875 11.0838 0.796729 12.4663 2.17927C13.8371 3.56181 14.5987 5.38958 14.5987 7.34623C14.5987 9.29116 13.8371 11.1189 12.4663 12.5015C11.0838 13.884 9.24429 14.6456 7.29936 14.6456ZM7.29936 13.0053C5.78793 13.0053 4.35852 12.4195 3.29233 11.3533C2.22613 10.2753 1.62859 8.85765 1.62859 7.34623C1.62859 5.82309 2.22613 4.4054 3.29233 3.32748C4.35852 2.26129 5.78793 1.67546 7.29936 1.67546C8.81078 1.67546 10.2402 2.26129 11.3064 3.32748C13.5208 5.5419 13.5208 9.13885 11.3064 11.3533C10.2402 12.4195 8.81078 13.0053 7.29936 13.0053Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M14.0368 15.3368C13.943 15.3368 13.8493 15.3016 13.779 15.2313L12.209 13.6613C12.4785 13.4504 12.7245 13.2395 12.9588 12.9935C13.2283 12.7357 13.4626 12.4545 13.6853 12.1616L15.267 13.7433C15.4076 13.8839 15.4076 14.1066 15.267 14.2472C15.1967 14.3057 15.1147 14.3409 15.0209 14.3409C14.9389 14.3409 14.8686 14.3175 14.81 14.2706L14.3531 14.833C14.4117 14.9619 14.3882 15.1142 14.2828 15.2313C14.2125 15.3016 14.1188 15.3368 14.0368 15.3368Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M18.4776 19.9531C18.0792 19.9531 17.6926 19.8008 17.4114 19.5079L14.1073 16.2156C13.8261 15.9344 13.6738 15.5477 13.6738 15.1494C13.6738 14.7393 13.8261 14.3526 14.1073 14.0714C14.4002 13.7903 14.7752 13.6262 15.1853 13.6262C15.5953 13.6262 15.9703 13.7903 16.2632 14.0714L19.5555 17.3638C20.1413 17.9613 20.1413 18.9221 19.5555 19.5079C19.2626 19.8008 18.8877 19.9531 18.4776 19.9531Z" fill="white" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.93229 9.71307C3.62004 8.40082 3.62004 6.28014 4.93229 4.9679C5.06117 4.8273 5.06117 4.60469 4.93229 4.47581C4.79169 4.33521 4.56908 4.33521 4.42848 4.47581C2.84676 6.05753 2.84676 8.62344 4.42848 10.2052C4.49878 10.2755 4.59251 10.3106 4.67452 10.3106C4.76826 10.3106 4.86199 10.2755 4.93229 10.2052C5.06117 10.0763 5.06117 9.85366 4.93229 9.71307Z" fill="white" />
                  </g>
                  <defs>
                    <clipPath id="clip0_163_465">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
          </div>



<div className="sm:hidden space-y-4">
  {filtered.length === 0 ? (
    <div className="max-w-md bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4 text-center py-16">
      <div className="flex flex-col items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
          {/* empty icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </div>

        <p className="text-sm text-gray-500 font-medium">
          No request quotes found
        </p>
      </div>
    </div>
  ) : (
    filtered.map((item, index) => (
      <div
        key={item._id || index}
        className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4"
      >
        {/* TOP */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-xs text-gray-500 uppercase">Status</p>
            <span
              className={`inline-flex mt-1 px-3 py-1 text-xs font-medium rounded-full border ${
                item.status === "request"
                  ? "bg-blue-100 text-blue-700 border-blue-200"
                  : "bg-red-100 text-red-700 border-red-200"
              }`}
            >
              {item.status === "request" ? "Requested" : item.status || "—"}
            </span>
          </div>


          <button
            type="button"
            onClick={() => openViewModal(item)}
            className="w-9 h-9 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>

        <div className="flex gap-2 justify-between border-t border-gray-100 pt-3">
          <div>
          <p className="text-xs text-gray-500 uppercase">Total Items</p>
          <p className="text-sm font-semibold text-gray-800">
            {item.line_items?.length || 0} items
          </p>
          </div>
                  <div className="">
          <p className="text-xs text-gray-500 uppercase">Requested Date</p>
                              <p className="text-sm text-gray-800 font-medium">
            {formatDate(item.createdAt)}
          </p>
                    <p className="text-xs text-gray-700">
            {formatTime(item.createdAt)}
          </p>
        </div>
        </div>

      </div>
    ))
  )}
</div>

          <div className="max-sm:!hidden">
          <div className="overflow-auto border border-gray-200 rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left py-3.5 px-4 font-semibold text-sm text-gray-800">
                  {[
                    "Actions",
                    // "Customer",
                    // "Email",
                    "Status",
                    "Total Items",
                    "Requested Date",
                  ].map((h) => (
                    <th key={h} className="text-left py-3 px-5">
                      <span className="text-[13px] lg:text-sm text-gray-800 font-semibold uppercase whitespace-nowrap">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr className="hover:bg-gray-50/50 transition-colors">
                    <td colSpan={6} className="py-3.5 px-4 text-center py-12">
                      <div className="flex flex-col items-center gap-6">
                        <div className="size-20 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-400"
                          >
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                          </svg>
                        </div>
                        <div className="max-w-xs mx-auto">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            No request quotes found
                          </h3>
                          <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                            It looks like you haven't made any quote requests yet. Start by browsing our products.
                          </p>
                          <button
                            onClick={() => navigate("/products")}
                            className="inline-flex items-center justify-center gap-2.5 bg-[#115B9D] hover:bg-[#0d4a80] text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-[0_8px_30px_rgb(17,91,157,0.2)] hover:shadow-[0_8px_30px_rgb(17,91,157,0.3)] transform  active:translate-y-0"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="12" y1="5" x2="12" y2="19"></line>
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                            Request Quote
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((item, index) => (
                    <tr
                      key={item._id || index}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                                            <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openViewModal(item)}
                            title="View"
                            className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-[#115B9D] transition-colors"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          </button>
                        </div>
                      </td>

                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex mt-1 px-3 py-1 text-sm font-medium capitalize border rounded-full ${item.status === "request"
                            ? "bg-blue-100 text-blue-700 border-blue-200"
                            : "bg-red-100 text-red-700 border-red-200"
                            }`}
                        >
                          {item.status === "request"
                            ? "requested"
                            : item?.status || "—"}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <p className="max-lg:text-sm text-md text-[#686666]">
                          {item.line_items?.length || 0} items
                        </p>
                      </td>
                      <td className="py-3.5 px-4">
                        <p className="max-lg:text-sm text-md text-gray-800 whitespace-nowrap">
                          {formatDate(item.createdAt)}
                        </p>
                        <p className="max-lg:text-xs text-sm text-gray-600">
                          {formatTime(item.createdAt)}
                        </p>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      </div>

      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeViewModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="size-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg
                          stroke="currentColor"
                          fill="currentColor"
                          stroke-width="0"
                          viewBox="0 0 384 512"
                          class="text-[#115B9D] w-5 h-5"
                          height="1em"
                          width="1em"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M288 256H96v64h192v-64zm89-151L279.1 7c-4.5-4.5-10.6-7-17-7H256v128h128v-6.1c0-6.3-2.5-12.4-7-16.9zm-153 31V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zM64 72c0-4.42 3.58-8 8-8h80c4.42 0 8 3.58 8 8v16c0 4.42-3.58 8-8 8H72c-4.42 0-8-3.58-8-8V72zm0 64c0-4.42 3.58-8 8-8h80c4.42 0 8 3.58 8 8v16c0 4.42-3.58 8-8 8H72c-4.42 0-8-3.58-8-8v-16zm256 304c0 4.42-3.58 8-8 8h-80c-4.42 0-8-3.58-8-8v-16c0-4.42 3.58-8 8-8h80c4.42 0 8 3.58 8 8v16zm0-200v96c0 8.84-7.16 16-16 16H80c-8.84 0-16-7.16-16-16v-96c0-8.84 7.16-16 16-16h224c8.84 0 16 7.16 16 16z"></path>
                        </svg>
                      </div>
                      <Dialog.Title className="text-base font-semibold text-gray-900">
                        Request Quote Details
                      </Dialog.Title>
                    </div>
                    <button
                      onClick={closeViewModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {selectedQuote && (
                    <div className="px-6 py-5 overflow-y-auto scrollbar flex-1 space-y-6">
                      <div className="flex items-center gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                        <div className="size-14 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                          <span className="text-lg font-bold text-[#115B9D]">
                            {getInitials(selectedQuote.customer_name)}
                          </span>
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900 capitalize">
                            {selectedQuote.customer_name || "—"}
                          </p>
                          <p className="text-sm text-gray-500">
                            {selectedQuote.email}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 border border-gray-100 rounded-xl bg-gray-50/30">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                            Billing Address
                          </p>
                          <p className="text-sm text-gray-700">
                            {selectedQuote.billingAddress?.bill_line1}
                          </p>
                          {selectedQuote.billingAddress?.bill_line2 && (
                            <p className="text-sm text-gray-700">
                              {selectedQuote.billingAddress?.bill_line2}
                            </p>
                          )}
                          <p className="text-sm text-gray-700">
                            {selectedQuote.billingAddress?.bill_city},{" "}
                            {selectedQuote.billingAddress?.bill_country}{" "}
                            {selectedQuote.billingAddress?.bill_postal_code}
                          </p>
                        </div>
                        <div className="p-4 border border-gray-100 rounded-xl bg-gray-50/30">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
                            Shipping Address
                          </p>
                          <p className="text-sm text-gray-700">
                            {selectedQuote.shippingAddress?.ship_line1}
                          </p>
                          {selectedQuote.shippingAddress?.ship_line2 && (
                            <p className="text-sm text-gray-700">
                              {selectedQuote.shippingAddress?.ship_line2}
                            </p>
                          )}
                          <p className="text-sm text-gray-700">
                            {selectedQuote.shippingAddress?.ship_city},{" "}
                            {selectedQuote.shippingAddress?.ship_country}{" "}
                            {selectedQuote.shippingAddress?.ship_postal_code}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                          Line Items
                        </p>
                        <div className="border border-gray-100 rounded-xl scrollbar overflow-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600 border-b border-gray-100">
                              <tr>
                                <th className="py-2.5 px-4 text-left font-medium">
                                  Item
                                </th>
                                <th className="py-2.5 px-4 text-center font-medium">
                                  Qty
                                </th>
                                <th className="py-2.5 px-4 text-right font-medium">
                                  Rate
                                </th>
                                <th className="py-2.5 px-4 text-right font-medium">
                                  Amount
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {selectedQuote.line_items?.map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2.5 px-4">
                                    <div className="min-w-[150px]">
                                      <p className="font-medium text-gray-800">
                                        {item.item_name || "--"}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {item.description || "--"}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="py-2.5 px-4 text-center text-gray-600">
                                    {item.qty || "1"}
                                  </td>
                                  <td className="py-2.5 px-4 text-right text-gray-600">
                                    {item.rate ? "$" : "--"}
                                    {item.rate}
                                  </td>
                                  <td className="py-2.5 px-4 text-right font-medium text-gray-800">
                                    {item.amount ? "$" : "--"}
                                    {item.amount}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                    <button
                      type="button"
                      onClick={closeViewModal}
                      className="px-5 py-2.5 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default RequestQuoteListing;
